/**
 * Modern POS Screen - Dark Mode
 * Inspired by GAS POS & E-commerce POS designs
 * Features:
 * - Dark mode with vibrant product cards
 * - Card-based product grid with color coding by category
 * - Modern shopping cart sidebar
 * - Quick actions and search
 * - Touch-friendly interface
 */
import React, { useState, useRef, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { RootState, AppDispatch } from '../../store/store';
import { 
  addToCart, 
  removeFromCart, 
  updateCartItemQty,
  updateCartItemDiscount,
  applyDiscount,
  clearCart,
  createSaleAsync 
} from './cashierSlice';
import { fetchItemsAsync } from '../inventory/inventorySlice';
import { Item } from '../../types/item';
import { PaymentDetailCreate, SaleCreate, SaleItemCreate } from '../../types/sale';
import PaymentModal from './PaymentModal';
import { ReceiptData } from '../../utils/printReceipt';
import ReceiptModal from '../../components/receipt/ReceiptModal';
import { getReceipt } from '../../services/saleService';
import { API_BASE_URL, PRICING_TIER_LABELS } from '../../utils/constants';
import POSViewSwitcher from '../../components/common/POSViewSwitcher';
import BarcodeScanner from '../../components/scanner/BarcodeScanner';
import CustomerSelector from '../../components/customer/CustomerSelector';
import { Customer } from '../../services/customerService';
import { calculateCartWithPricing, type PaymentMethod as PricingPaymentMethod } from '../../utils/cartPricing';
import { getTierDescription } from '../../utils/pricingTier';
import { thermalPrinter, type ReceiptData as ThermalReceiptData } from '../../services/thermalPrinterService';
import { bluetoothPrinter } from '../../services/bluetoothPrinterService';
import { whatsappService } from '../../services/whatsappService';

// Category color mapping (like GAS POS fuel colors)
const categoryColors: { [key: string]: { bg: string; text: string; accent: string } } = {
  'LCD': { bg: 'from-emerald-400 to-teal-500', text: 'text-white', accent: 'bg-emerald-500' },
  'Battery': { bg: 'from-blue-400 to-blue-600', text: 'text-white', accent: 'bg-blue-500' },
  'Flexible': { bg: 'from-amber-400 to-orange-500', text: 'text-white', accent: 'bg-amber-500' },
  'IC': { bg: 'from-purple-400 to-purple-600', text: 'text-white', accent: 'bg-purple-500' },
  'Connector': { bg: 'from-pink-400 to-rose-500', text: 'text-white', accent: 'bg-pink-500' },
  'Tools': { bg: 'from-gray-400 to-gray-600', text: 'text-white', accent: 'bg-gray-500' },
  'Accessories': { bg: 'from-cyan-400 to-blue-500', text: 'text-white', accent: 'bg-cyan-500' },
  'Default': { bg: 'from-slate-400 to-slate-600', text: 'text-white', accent: 'bg-slate-500' },
};

const getCategoryColor = (category: string) => {
  const normalized = category.toUpperCase();
  for (const key in categoryColors) {
    if (normalized.includes(key.toUpperCase())) {
      return categoryColors[key];
    }
  }
  return categoryColors['Default'];
};

const ModernPOSScreen: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { cartItems, subtotal, discount, total, loading } = useSelector((state: RootState) => state.cashier);
  const { items } = useSelector((state: RootState) => state.inventory);
  const currentUser = useSelector((state: RootState) => state.auth.user);
  
  const [barcode, setBarcode] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('All');
  const [showPayment, setShowPayment] = useState(false);
  const [showScanner, setShowScanner] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<number | null>(null);
  const [customerData, setCustomerData] = useState<Customer | null>(null);
  const [manualCustomerName, setManualCustomerName] = useState<string>(''); // Nama manual untuk retail walk-in
  const [paymentMethod, setPaymentMethod] = useState<PricingPaymentMethod>('cash');
  const [paymentDueDays, setPaymentDueDays] = useState<number>(0);
  const [showReceipt, setShowReceipt] = useState(false);
  const [receiptData, setReceiptData] = useState<ReceiptData | null>(null);
  const [printerConnected, setPrinterConnected] = useState(false);
  const [printerInfo, setPrinterInfo] = useState<string | null>(null);
  const [printerType, setPrinterType] = useState<'usb' | 'bluetooth' | null>(null);
  const [autoPrint, setAutoPrint] = useState(true);
  const [showPrinterSettings, setShowPrinterSettings] = useState(false);
  
  const navigate = useNavigate();
  const barcodeInputRef = useRef<HTMLInputElement>(null);
  
  useEffect(() => {
    dispatch(fetchItemsAsync());
  }, [dispatch]);
  
  useEffect(() => {
    barcodeInputRef.current?.focus();
  }, []);
  
  // Auto-connect to previously authorized thermal printer
  useEffect(() => {
    const autoConnectPrinter = async () => {
      // Try USB first
      try {
        const connected = await thermalPrinter.autoConnect();
        if (connected) {
          setPrinterConnected(true);
          setPrinterInfo(thermalPrinter.getPrinterInfo());
          setPrinterType('usb');
          console.log('✅ USB thermal printer auto-connected');
          return;
        }
      } catch (error) {
        console.log('ℹ️ No USB printer available');
      }
      
      // Try Bluetooth if USB not available
      try {
        const connected = await bluetoothPrinter.autoConnect();
        if (connected) {
          setPrinterConnected(true);
          setPrinterInfo(bluetoothPrinter.getPrinterInfo());
          setPrinterType('bluetooth');
          console.log('✅ Bluetooth printer auto-connected');
        }
      } catch (error) {
        console.log('ℹ️ No Bluetooth printer available');
      }
    };
    
    autoConnectPrinter();
  }, []);
  
  const handleBarcodeKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && barcode.trim()) {
      e.preventDefault();
      handleBarcodeScanned(barcode.trim());
      setBarcode('');
    }
  };
  
  const handleBarcodeScanned = (code: string) => {
    const item = items.find(i => i.barcode === code || i.sku === code);
    if (item) {
      dispatch(addToCart({ item, qty: 1 }));
    } else {
      alert(`Item dengan barcode ${code} tidak ditemukan`);
    }
  };
  
  const getItemImageUrl = (item: Item) => {
    if (!item.image?.url) return null;
    
    // Handle relative URLs (uploaded images)
    if (item.image.url.startsWith('/api/v1/')) {
      const pathAfterApiV1 = item.image.url.substring(7);
      return `${API_BASE_URL}${pathAfterApiV1}`;
    }
    // External URLs (direct links)
    return item.image.url;
  };
  
  const handleAddToCart = (item: Item) => {
    const stock = item.stock ?? 0;
    if (stock <= 0) {
      alert(`${item.name} habis! Stock: 0`);
      return;
    }
    dispatch(addToCart({ item, qty: 1 }));
  };
  
  const handleCheckout = () => {
    if (cartItems.length === 0) {
      alert('Keranjang kosong!');
      return;
    }
    
    // Validate gold tier tempo rules
    if (customerData?.pricing_tier === 'gold' && paymentMethod === 'tempo') {
      if (paymentDueDays > 7) {
        alert('⚠️ Gold tier hanya dapat tempo maksimal 7 hari!\n\nDiskon tempo gold: 5%\nDiskon cash gold: 10%');
        return;
      }
    }
    
    setShowPayment(true);
  };
  
  const handlePaymentComplete = async (payments: PaymentDetailCreate[]) => {
    // Calculate pricing tier adjusted items
    let saleItems: SaleItemCreate[];
    let finalDiscount = 0;
    
    if (customerData) {
      // Use pricing tier calculation
      const cartPricing = calculateCartWithPricing(
        cartItems,
        customerData,
        paymentMethod,
        paymentDueDays
      );
      
      // Map items with pricing tier adjusted prices
      saleItems = cartPricing.items.map(pricedItem => {
        const cartItem = cartItems.find(ci => ci.item.id === pricedItem.itemId);
        return {
          item_id: pricedItem.itemId,  // ✅ Correct field name
          qty: pricedItem.quantity,
          price: Number(pricedItem.unitPrice.toFixed(2)),  // Pricing tier adjusted price, rounded to 2 decimals
          discount: Number(cartItem?.discount) || 0
        };
      });
      
      // Backend will calculate: subtotal = sum(price * qty) - discount
      // Our prices already include tier adjustment, so discount should be 0
      // UNLESS there's a manual per-item discount
      const manualItemDiscounts = cartItems.reduce((sum, ci) => sum + (ci.discount || 0), 0);
      finalDiscount = Number(manualItemDiscounts.toFixed(2));
    } else {
      // No customer - use base prices
      saleItems = cartItems.map(ci => ({
        item_id: ci.item.id,
        qty: ci.qty,
        price: Number(ci.price),  // Base sell_price
        discount: Number(ci.discount) || 0
      }));
      
      finalDiscount = Number(discount) || 0;
    }
    
    // Determine primary payment method
    const primaryPaymentMethod = payments.length > 0 
      ? payments[0].method 
      : PaymentMethod.CASH;
    
    const totalPaid = payments.reduce((sum, p) => sum + p.amount, 0);
    
    const saleData: SaleCreate = {
      items: saleItems,
      payment_method: primaryPaymentMethod,  // Enum already lowercase
      customer_id: selectedCustomer || undefined,
      discount: finalDiscount,  // Calculated discount from pricing tier
      payment_details: payments.length > 1 ? payments : undefined,  // Only for split payments
      amount_paid: Number(totalPaid),  // Ensure number
      notes: customerData?.name ? `Customer: ${customerData.name}` : undefined,
      customer_name: customerData?.name,
      customer_phone: customerData?.phone,
    };
    
    console.log('📤 Sending sale data:', saleData);
    console.log('📦 Sale data stringified:', JSON.stringify(saleData, null, 2));
    console.log('🔍 Payment method value:', primaryPaymentMethod, '(type:', typeof primaryPaymentMethod, ')');
    console.log('💰 Items:', saleItems);
    console.log('🧮 Discount:', finalDiscount);
    console.log('💵 Amount paid:', totalPaid);
    
    try {
      const result = await dispatch(createSaleAsync({ 
        saleData, 
        user: currentUser, 
        branchId: currentUser?.branch_id 
      })).unwrap();
      console.log('✅ Sale created:', result);
      
      // Clear cart and close payment modal
      setShowPayment(false);
      
      // Refresh items to update stock display
      dispatch(fetchItemsAsync());
      
      // Get receipt data and show modal
      if (result.sale && result.sale.id && result.sale.id > 0) {
        try {
          const receipt = await getReceipt(result.sale.id);
          
          // Enhance receipt with pricing tier information
          if (customerData?.pricing_tier) {
            const cartPricing = calculateCartWithPricing(
              cartItems,
              customerData,
              paymentMethod as PricingPaymentMethod,
              paymentDueDays
            );
            
            receipt.customer_pricing_tier = customerData.pricing_tier;
            receipt.pricing_tier_label = PRICING_TIER_LABELS[customerData.pricing_tier];
            receipt.pricing_breakdown = {
              base_total: cartPricing.subtotal,
              discount_rate: 0,
              discount_amount: cartPricing.totalDiscount,
              markup_rate: 0,
              markup_amount: 0,
              final_total: cartPricing.total
            };
          }
          
          setReceiptData(receipt);
          setShowReceipt(true);
          
          // Auto-print to thermal printer if enabled
          if (autoPrint) {
            setTimeout(() => {
              handlePrintReceipt(receipt);
            }, 500); // Small delay to ensure receipt modal is ready
          }
        } catch (printError: any) {
          console.error('Error fetching receipt:', printError);
          alert(`✅ TRANSAKSI BERHASIL!\n\nInvoice: ${result.sale.invoice_no}\n(Gagal menampilkan receipt)`);
        }
      } else {
        // Offline sale
        alert('✅ Transaksi disimpan offline! Akan disync saat online.');
      }
    } catch (error: any) {
      console.error('❌ Sale creation error:', error);
      console.error('Error type:', typeof error);
      console.error('Error is array:', Array.isArray(error));
      
      // Safely extract error message
      let errorMessage = 'Failed to create sale';
      
      // Check if error is array of validation errors (from rejectWithValue)
      if (Array.isArray(error)) {
        // FastAPI validation errors array
        errorMessage = error.map((e: any) => {
          if (typeof e === 'string') return e;
          if (e.loc && e.msg) {
            return `${e.loc.join(' → ')}: ${e.msg}`;
          }
          return JSON.stringify(e);
        }).join('\n');
      } else if (typeof error === 'string') {
        // Simple string error
        errorMessage = error;
      } else if (error?.response?.data?.detail) {
        // Axios error with response
        const detail = error.response.data.detail;
        if (typeof detail === 'string') {
          errorMessage = detail;
        } else if (Array.isArray(detail)) {
          errorMessage = detail.map((e: any) => 
            `${e.loc?.join(' → ') || 'Field'}: ${e.msg}`
          ).join('\n');
        } else {
          errorMessage = JSON.stringify(detail, null, 2);
        }
      } else if (error?.message) {
        errorMessage = error.message;
      } else if (error?.code) {
        errorMessage = `Network error: ${error.code}`;
      }
      
      // Show error alert
      if (errorMessage.includes('\n')) {
        alert(`❌ Validation Error:\n${errorMessage}`);
      } else {
        alert(`❌ Transaksi Gagal: ${errorMessage}`);
      }
    }
  };
  
  const handleCloseReceipt = () => {
    setShowReceipt(false);
    setReceiptData(null);
  };
  
  // Printer functions
  const handleConnectUSBPrinter = async () => {
    try {
      // Disconnect Bluetooth if connected
      if (printerType === 'bluetooth') {
        await bluetoothPrinter.disconnect();
      }
      
      await thermalPrinter.requestPrinter();
      setPrinterConnected(true);
      setPrinterInfo(thermalPrinter.getPrinterInfo());
      setPrinterType('usb');
      alert('✅ USB Printer berhasil terhubung!\n\n' + thermalPrinter.getPrinterInfo());
    } catch (error: any) {
      // Don't show alert if user just cancelled
      if (!error.message?.includes('tidak dipilih')) {
        alert('❌ Gagal terhubung ke USB printer:\n' + error.message);
      }
    }
  };
  
  const handleConnectBluetoothPrinter = async () => {
    try {
      // Disconnect USB if connected
      if (printerType === 'usb') {
        await thermalPrinter.disconnect();
      }
      
      await bluetoothPrinter.requestPrinter();
      setPrinterConnected(true);
      setPrinterInfo(bluetoothPrinter.getPrinterInfo());
      setPrinterType('bluetooth');
      alert('✅ Bluetooth Printer berhasil terhubung!\n\n' + bluetoothPrinter.getPrinterInfo());
    } catch (error: any) {
      // Don't show alert if user just cancelled
      if (!error.message?.includes('tidak dipilih')) {
        alert('❌ Gagal terhubung ke Bluetooth printer:\n' + error.message);
      }
    }
  };
  
  const handleDisconnectPrinter = async () => {
    if (printerType === 'usb') {
      await thermalPrinter.disconnect();
    } else if (printerType === 'bluetooth') {
      await bluetoothPrinter.disconnect();
    }
    setPrinterConnected(false);
    setPrinterInfo(null);
    setPrinterType(null);
  };
  
  const handleTestPrint = async () => {
    if (!printerConnected) {
      alert('❌ Printer belum terhubung!');
      return;
    }
    
    try {
      if (printerType === 'usb') {
        await thermalPrinter.testPrint();
      } else if (printerType === 'bluetooth') {
        await bluetoothPrinter.testPrint();
      }
      alert('✅ Test print berhasil!');
    } catch (error: any) {
      alert('❌ Test print gagal:\n' + error.message);
    }
  };
  
  const convertReceiptDataForThermalPrint = (receipt: ReceiptData): ThermalReceiptData => {
    const cartPricing = calculateCartWithPricing(
      cartItems,
      customerData,
      paymentMethod,
      paymentDueDays
    );
    
    // Get printer settings from receipt backend (printer_settings)
    const printerSettings = (receipt as any).printer_settings || {};
    const paperWidth = printerSettings.printer_paper_width || printerSettings.paper_width || 80;
    
    return {
      invoiceNumber: receipt.invoice_no,
      date: new Date(receipt.created_at).toLocaleString('id-ID'),
      cashier: receipt.cashier?.name || currentUser?.name || 'Cashier',
      customer: customerData ? {
        name: customerData.name,
        phone: customerData.phone,
        tier: PRICING_TIER_LABELS[customerData.pricing_tier]
      } : (manualCustomerName ? {
        // Virtual customer with manual name
        name: manualCustomerName,
        phone: '',
        tier: 'Retail +35%'
      } : undefined), // No customer name = undefined (will show "Pelanggan" in printer)
      items: receipt.items.map(item => ({
        name: item.name,  // ✅ Backend ReceiptItemData uses 'name' not 'item_name'
        quantity: item.qty,
        price: item.price,
        subtotal: item.qty * item.price
      })),
      subtotal: cartPricing.subtotal,
      discount: cartPricing.totalDiscount > 0 ? cartPricing.totalDiscount : undefined,
      markup: undefined, // Will calculate from pricing
      total: receipt.total,
      paymentMethod: receipt.payment_method,
      paid: receipt.amount_paid,
      change: receipt.change_amount,
      dueDate: receipt.payment_due_date ? new Date(receipt.payment_due_date).toLocaleDateString('id-ID') : undefined,
      branchName: receipt.branch?.name || 'PhoneLabs',
      paperWidth: paperWidth,
      continuousMode: true, // Always use continuous mode (no auto-cut)
      headerText: printerSettings.header || printerSettings.printer_header, // Custom header from backend
      footerText: printerSettings.footer || printerSettings.printer_footer, // Custom footer from backend
      logoBase64: printerSettings.logo_base64, // Logo from backend
    } as any;
  };
  
  const handlePrintReceipt = async (receipt: ReceiptData) => {
    // Try printer if connected and auto-print enabled
    if (printerConnected && autoPrint) {
      try {
        const thermalData = convertReceiptDataForThermalPrint(receipt);
        
        if (printerType === 'usb') {
          await thermalPrinter.printReceipt(thermalData);
          console.log('✅ Printed to USB thermal printer');
        } else if (printerType === 'bluetooth') {
          await bluetoothPrinter.printReceipt(thermalData);
          console.log('✅ Printed to Bluetooth printer');
        }
        
        return true;
      } catch (error) {
        console.error('❌ Print failed:', error);
        
        // Fallback to WhatsApp if customer has phone
        if (customerData?.phone && whatsappService.isValidPhoneNumber(customerData.phone)) {
          const sendToWhatsApp = confirm(
            `❌ Print gagal!\n\n` +
            `📱 Kirim nota via WhatsApp ke customer?\n` +
            `(${customerData.phone})`
          );
          
          if (sendToWhatsApp) {
            const thermalData = convertReceiptDataForThermalPrint(receipt);
            whatsappService.sendReceipt(customerData.phone, thermalData);
            return true;
          }
        }
        
        return false;
      }
    }
    
    return false;
  };
  
  // Get unique categories
  const categories = ['All', ...Array.from(new Set(items.map(i => i.category)))];
  
  // Calculate final total with pricing tier
  const finalTotal = customerData 
    ? calculateCartWithPricing(
        cartItems,
        customerData,
        paymentMethod,
        paymentDueDays
      ).total
    : total; // Fallback to Redux total if no customer
  
  // Filter items
  const filteredItems = items.filter(item => {
    const matchesSearch = item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         item.sku.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'All' || item.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });
  
  return (
    <div className="flex h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
      {/* View Switcher - Floating */}
      <POSViewSwitcher />
      
      {/* Side Menu Drawer */}
      {showMenu && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 bg-black/50 z-40"
            onClick={() => setShowMenu(false)}
          />
          
          {/* Menu Drawer */}
          <div className="fixed left-0 top-0 bottom-0 w-80 bg-gray-900 z-50 shadow-2xl overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-8">
                <h2 className="text-xl font-bold text-white">📋 Menu</h2>
                <button
                  onClick={() => setShowMenu(false)}
                  className="text-gray-400 hover:text-white"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              <nav className="space-y-2">
                <button
                  onClick={() => { navigate('/pos'); setShowMenu(false); }}
                  className="w-full text-left px-4 py-3 rounded-lg bg-blue-600 text-white font-medium"
                >
                  💰 Kasir / POS
                </button>
                
                <button
                  onClick={() => { navigate('/inventory'); setShowMenu(false); }}
                  className="w-full text-left px-4 py-3 rounded-lg text-gray-300 hover:bg-gray-800 transition-colors"
                >
                  📦 Inventory
                </button>
                
                <button
                  onClick={() => { navigate('/customers'); setShowMenu(false); }}
                  className="w-full text-left px-4 py-3 rounded-lg text-gray-300 hover:bg-gray-800 transition-colors"
                >
                  👥 Customers
                </button>
                
                <button
                  onClick={() => { navigate('/stock'); setShowMenu(false); }}
                  className="w-full text-left px-4 py-3 rounded-lg text-gray-300 hover:bg-gray-800 transition-colors"
                >
                  📊 Stock Management
                </button>
                
                <button
                  onClick={() => { navigate('/purchase-orders'); setShowMenu(false); }}
                  className="w-full text-left px-4 py-3 rounded-lg text-gray-300 hover:bg-gray-800 transition-colors"
                >
                  🛒 Purchase Orders
                </button>
                
                <button
                  onClick={() => { navigate('/mutations'); setShowMenu(false); }}
                  className="w-full text-left px-4 py-3 rounded-lg text-gray-300 hover:bg-gray-800 transition-colors"
                >
                  🔄 Mutations
                </button>
                
                <button
                  onClick={() => { navigate('/returns'); setShowMenu(false); }}
                  className="w-full text-left px-4 py-3 rounded-lg text-gray-300 hover:bg-gray-800 transition-colors"
                >
                  ↩️ Returns
                </button>
                
                <button
                  onClick={() => { navigate('/reports'); setShowMenu(false); }}
                  className="w-full text-left px-4 py-3 rounded-lg text-gray-300 hover:bg-gray-800 transition-colors"
                >
                  📈 Reports
                </button>
                
                <button
                  onClick={() => { navigate('/print-settings'); setShowMenu(false); }}
                  className="w-full text-left px-4 py-3 rounded-lg text-gray-300 hover:bg-gray-800 transition-colors"
                >
                  🖨️ Print Settings
                </button>
                
                {currentUser?.role === 'owner' && (
                  <button
                    onClick={() => { navigate('/branches'); setShowMenu(false); }}
                    className="w-full text-left px-4 py-3 rounded-lg text-gray-300 hover:bg-gray-800 transition-colors"
                  >
                    🏪 Branches
                  </button>
                )}
              </nav>
            </div>
          </div>
        </>
      )}
      
      {/* Main Content */}
      <div className="flex-1 flex flex-col">
        {/* Header */}
        <header className="bg-gray-900/80 backdrop-blur-sm border-b border-gray-700 px-6 py-4">
          <div className="flex items-center justify-between">
            {/* Hamburger Menu & Logo */}
            <div className="flex items-center gap-4">
              <button
                onClick={() => setShowMenu(!showMenu)}
                className="text-white hover:text-blue-400 transition-colors p-2"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>
              <div>
                <h1 className="text-2xl font-bold text-white">📱 Sparepart Ponsel</h1>
                <p className="text-sm text-gray-400 mt-1">Sparepart & Accessories</p>
              </div>
            </div>
            
            {/* User Info */}
            <div className="flex items-center gap-4">
              {/* Printer Status Indicator */}
              <button
                onClick={() => setShowPrinterSettings(!showPrinterSettings)}
                className={`px-4 py-2 rounded-lg font-medium transition-all flex items-center gap-2 ${
                  printerConnected
                    ? 'bg-green-600 hover:bg-green-700 text-white'
                    : 'bg-gray-700 hover:bg-gray-600 text-gray-300'
                }`}
                title={printerConnected ? `Printer: ${printerInfo}` : 'Printer tidak terhubung'}
              >
                <span>{printerConnected ? '🖨️' : '⚠️'}</span>
                <span className="text-sm">
                  {printerConnected ? 'Printer OK' : 'No Printer'}
                </span>
              </button>
              
              <div className="text-right">
                <div className="text-sm text-gray-400">Kasir</div>
                <div className="font-medium text-white">{currentUser?.full_name || 'Admin'}</div>
              </div>
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-400 to-purple-500 flex items-center justify-center text-white font-bold">
                {currentUser?.full_name?.[0] || 'A'}
              </div>
            </div>
          </div>
        </header>
        
        {/* Search & Scanner */}
        <div className="px-6 py-4 bg-gray-800/50">
          <div className="grid grid-cols-2 gap-4">
            {/* Search */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                🔍 Cari Produk
              </label>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Ketik nama atau SKU produk..."
                className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            
            {/* Barcode Scanner */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                📷 Scan Barcode
              </label>
              <div className="flex gap-2">
                <input
                  ref={barcodeInputRef}
                  type="text"
                  value={barcode}
                  onChange={(e) => setBarcode(e.target.value)}
                  onKeyDown={handleBarcodeKeyDown}
                  placeholder="Scan barcode atau ketik SKU..."
                  className="flex-1 px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-green-500 focus:border-transparent font-mono"
                />
                <button
                  onClick={() => setShowScanner(true)}
                  className="px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center"
                  title="Open Camera Scanner"
                >
                  📷
                </button>
              </div>
            </div>
          </div>
          
          {/* Category Filters */}
          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-300 mb-2">
              📂 Kategori
            </label>
            <div className="flex gap-2 flex-wrap">
              {categories.map(cat => {
                const color = getCategoryColor(cat);
                return (
                  <button
                    key={cat}
                    onClick={() => setSelectedCategory(cat)}
                    className={`px-4 py-2 rounded-lg font-medium transition-all ${
                      selectedCategory === cat
                        ? `bg-gradient-to-r ${color.bg} ${color.text} shadow-lg scale-105`
                        : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                    }`}
                  >
                    {cat}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
        
        {/* Product Grid */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
            {filteredItems.map(item => {
              const stock = item.stock ?? 0;
              const color = getCategoryColor(item.category);
              
              return (
                <div
                  key={item.id}
                  onClick={() => handleAddToCart(item)}
                  className={`relative rounded-2xl p-4 cursor-pointer transition-all hover:scale-105 hover:shadow-2xl overflow-hidden ${
                    stock > 0 
                      ? `bg-gradient-to-br ${color.bg}` 
                      : 'bg-gray-700 opacity-50 cursor-not-allowed'
                  }`}
                >
                  {/* Product Image Background */}
                  {item.image && (
                    <div className="absolute inset-0 opacity-20 overflow-hidden">
                      <img
                        src={getItemImageUrl(item)}
                        alt={item.name}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          (e.target as HTMLImageElement).style.display = 'none';
                        }}
                      />
                    </div>
                  )}
                  
                  {/* Category Badge */}
                  <div className={`absolute top-2 right-2 px-2 py-1 rounded-lg text-xs font-bold ${
                    stock > 0 ? 'bg-white/20 text-white' : 'bg-gray-600 text-gray-300'
                  }`}>
                    {item.category}
                  </div>
                  
                  {/* Stock Badge */}
                  <div className={`absolute top-2 left-2 px-2 py-1 rounded-lg text-xs font-bold ${
                    stock === 0 ? 'bg-red-500 text-white' :
                    stock < 5 ? 'bg-yellow-500 text-black' :
                    'bg-green-500 text-white'
                  }`}>
                    {stock === 0 ? 'HABIS' : `${stock} pcs`}
                  </div>
                  
                  {/* Product Name */}
                  <div className="mt-8 mb-2">
                    <h3 className={`font-bold text-lg line-clamp-2 ${stock > 0 ? 'text-white' : 'text-gray-400'}`}>
                      {item.name}
                    </h3>
                    <p className={`text-xs mt-1 ${stock > 0 ? 'text-white/70' : 'text-gray-500'}`}>
                      {item.sku}
                    </p>
                  </div>
                  
                  {/* Price */}
                  <div className="mt-4 flex items-center justify-between">
                    <div>
                      <div className={`text-2xl font-bold ${stock > 0 ? 'text-white' : 'text-gray-400'}`}>
                        Rp {(Number(item.sell_price) / 1000).toFixed(0)}k
                      </div>
                      <div className={`text-xs ${stock > 0 ? 'text-white/60' : 'text-gray-500'}`}>
                        Rp {Number(item.sell_price).toLocaleString()}
                      </div>
                    </div>
                    
                    {stock > 0 && (
                      <button className="w-10 h-10 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center text-white font-bold text-xl">
                        +
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          
          {filteredItems.length === 0 && (
            <div className="text-center py-12">
              <div className="text-6xl mb-4">🔍</div>
              <p className="text-gray-400 text-lg">Tidak ada produk ditemukan</p>
              <p className="text-gray-500 text-sm mt-2">Coba ubah filter atau kata kunci pencarian</p>
            </div>
          )}
        </div>
      </div>
      
      {/* Shopping Cart Sidebar */}
      <div className="w-96 bg-gray-900 border-l border-gray-700 flex flex-col shadow-2xl">
        {/* Cart Header */}
        <div className="px-6 py-4 border-b border-gray-700">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-xl font-bold text-white">🛒 Keranjang</h2>
            <span className="px-3 py-1 bg-blue-500 text-white rounded-full text-sm font-bold">
              {cartItems.length}
            </span>
          </div>
          {cartItems.length > 0 && (
            <button
              onClick={() => dispatch(clearCart())}
              className="text-sm text-red-400 hover:text-red-300"
            >
              🗑️ Hapus Semua
            </button>
          )}
        </div>
        
        {/* Customer Selector */}
        <div className="px-6 py-4 border-b border-gray-700">
          <CustomerSelector
            selectedCustomerId={selectedCustomer}
            onCustomerSelect={(customerId, customer) => {
              setSelectedCustomer(customerId);
              setCustomerData(customer);
              if (customerId !== 0) {
                setManualCustomerName(''); // Clear manual name if real customer selected
              }
            }}
            darkMode={true}
          />
          
          {/* Manual customer name for retail walk-in (id: 0) */}
          {selectedCustomer === 0 && (
            <div className="mt-3">
              <label className="block text-xs text-gray-400 mb-1">
                Nama Pembeli (opsional)
              </label>
              <input
                type="text"
                value={manualCustomerName}
                onChange={(e) => setManualCustomerName(e.target.value)}
                placeholder="Pelanggan"
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm focus:border-blue-500 focus:outline-none"
              />
            </div>
          )}
        </div>
        
        {/* Cart Items */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {cartItems.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-6xl mb-4">🛍️</div>
              <p className="text-gray-400">Keranjang kosong</p>
              <p className="text-gray-500 text-sm mt-2">Pilih produk untuk memulai</p>
            </div>
          ) : (
            <div className="space-y-3">
              {cartItems.map((ci) => {
                const color = getCategoryColor(ci.item.category);
                return (
                  <div key={ci.item.id} className="bg-gray-800 rounded-lg p-4 border border-gray-700">
                    {/* Item Header */}
                    <div className="flex justify-between items-start mb-3">
                      <div className="flex-1">
                        <h4 className="font-semibold text-white text-sm">
                          {ci.item.sku}
                        </h4>
                      </div>
                      <button
                        onClick={() => dispatch(removeFromCart(ci.item.id))}
                        className="ml-2 w-6 h-6 rounded-full bg-red-500/20 hover:bg-red-500 text-red-400 hover:text-white flex items-center justify-center transition-colors"
                      >
                        ×
                      </button>
                    </div>
                    
                    {/* Quantity Controls */}
                    <div className="flex items-center gap-3 mb-3">
                      <button
                        onClick={() => dispatch(updateCartItemQty({ itemId: ci.item.id, qty: Math.max(1, ci.qty - 1) }))}
                        className="w-8 h-8 rounded-lg bg-gray-700 hover:bg-gray-600 text-white flex items-center justify-center font-bold"
                      >
                        −
                      </button>
                      <input
                        type="number"
                        min="1"
                        value={ci.qty}
                        onChange={(e) => dispatch(updateCartItemQty({ itemId: ci.item.id, qty: parseInt(e.target.value) || 1 }))}
                        className="w-16 px-2 py-1 bg-gray-700 border border-gray-600 rounded-lg text-white text-center font-mono"
                      />
                      <button
                        onClick={() => dispatch(updateCartItemQty({ itemId: ci.item.id, qty: ci.qty + 1 }))}
                        className="w-8 h-8 rounded-lg bg-gray-700 hover:bg-gray-600 text-white flex items-center justify-center font-bold"
                      >
                        +
                      </button>
                      <span className="text-gray-400 text-sm">
                        × Rp {ci.price.toLocaleString()}
                      </span>
                    </div>
                    
                    {/* Discount */}
                    <div className="flex items-center gap-2 mb-3">
                      <label className="text-xs text-gray-400">Diskon:</label>
                      <input
                        type="number"
                        min="0"
                        value={ci.discount}
                        onChange={(e) => dispatch(updateCartItemDiscount({ itemId: ci.item.id, discount: parseFloat(e.target.value) || 0 }))}
                        className="flex-1 px-2 py-1 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm"
                        placeholder="0"
                      />
                    </div>
                    
                    {/* Subtotal */}
                    <div className="flex justify-between items-center pt-3 border-t border-gray-700">
                      <span className="text-sm text-gray-400">Subtotal</span>
                      <span className="text-lg font-bold text-white">
                        Rp {((ci.price * ci.qty) - ci.discount).toLocaleString()}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
        
        {/* Cart Summary */}
        <div className="border-t border-gray-700 px-6 py-4 bg-gray-900">
          {/* Payment Method Selector */}
          {customerData && (
            <div className="mb-4 p-4 bg-gray-800 rounded-lg border border-gray-700">
              <label className="block text-sm font-medium text-gray-300 mb-2">
                💳 Metode Pembayaran
              </label>
              <select
                value={paymentMethod}
                onChange={(e) => setPaymentMethod(e.target.value as PricingPaymentMethod)}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm focus:ring-2 focus:ring-blue-500"
              >
                <option value="cash">💵 Cash</option>
                <option value="tempo">📅 Tempo</option>
                <option value="transfer">🏦 Transfer</option>
              </select>
              
              {paymentMethod === 'tempo' && (
                <div className="mt-3">
                  <label className="block text-sm text-gray-400 mb-1">
                    📆 Jatuh Tempo (hari)
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="90"
                    value={paymentDueDays}
                    onChange={(e) => setPaymentDueDays(parseInt(e.target.value) || 0)}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm focus:ring-2 focus:ring-blue-500"
                    placeholder="Masukkan jumlah hari"
                  />
                  {customerData.pricing_tier === 'gold' && paymentDueDays > 7 && (
                    <p className="mt-2 text-xs text-red-400">
                      ⚠️ Gold tier hanya dapat tempo maksimal 7 hari!
                    </p>
                  )}
                </div>
              )}
            </div>
          )}
          
          {/* Pricing Display with Tier */}
          {customerData ? (
            (() => {
              const cartPricing = calculateCartWithPricing(
                cartItems,
                customerData,
                paymentMethod as PricingPaymentMethod,
                paymentDueDays
              );
              
              return (
                <div className="space-y-2 mb-4">
                  {/* Tier Badge */}
                  <div className="flex items-center gap-2 p-3 bg-gradient-to-r from-blue-500/20 to-purple-500/20 rounded-lg border border-blue-500/30">
                    <span className="text-2xl">👤</span>
                    <div className="flex-1">
                      <div className="font-bold text-white">
                        {PRICING_TIER_LABELS[customerData.pricing_tier]}
                      </div>
                      <div className="text-xs text-gray-300">
                        {getTierDescription(customerData.pricing_tier, paymentMethod, paymentDueDays)}
                      </div>
                    </div>
                  </div>
                  
                  {/* Pricing Breakdown */}
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-400">Subtotal (Harga Normal)</span>
                      <span className="text-white font-medium">
                        Rp {cartPricing.subtotal.toLocaleString()}
                      </span>
                    </div>
                    
                    {cartPricing.totalDiscount > 0 && (
                      <div className="flex justify-between text-sm">
                        <span className="text-orange-400">
                          Markup (+35%)
                        </span>
                        <span className="text-orange-400 font-medium">
                          + Rp {cartPricing.totalDiscount.toLocaleString()}
                        </span>
                      </div>
                    )}
                    
                    {cartPricing.totalDiscount < 0 && (
                      <div className="flex justify-between text-sm">
                        <span className="text-green-400">
                          Diskon Tier
                        </span>
                        <span className="text-green-400 font-medium">
                          - Rp {Math.abs(cartPricing.totalDiscount).toLocaleString()}
                        </span>
                      </div>
                    )}
                    
                    <div className="flex justify-between text-2xl font-bold pt-3 border-t border-gray-700">
                      <span className="text-white">Total</span>
                      <span className="text-green-400">
                        Rp {cartPricing.total.toLocaleString()}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })()
          ) : (
            // Fallback: No customer selected (original display)
            <div className="space-y-2 mb-4">
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Subtotal</span>
                <span className="text-white font-medium">Rp {subtotal.toLocaleString()}</span>
              </div>
              
              {/* Total Discount Input */}
              <div className="space-y-2 py-2 border-t border-gray-700 pt-3">
                <div className="flex items-center gap-2">
                  <label className="text-sm text-gray-400 whitespace-nowrap">💰 Diskon (Rp):</label>
                  <input
                    type="number"
                    min="0"
                    max={subtotal}
                    value={discount}
                    onChange={(e) => {
                      const value = parseFloat(e.target.value) || 0;
                      dispatch(applyDiscount({ discount: value }));
                    }}
                    className="flex-1 px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm focus:ring-2 focus:ring-yellow-500"
                    placeholder="0"
                  />
                </div>
                
                <div className="flex items-center gap-2">
                  <label className="text-sm text-gray-400 whitespace-nowrap">📊 Diskon (%):</label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="0.1"
                    onChange={(e) => {
                      const percentage = parseFloat(e.target.value) || 0;
                      if (percentage > 0) {
                        dispatch(applyDiscount({ percentage }));
                      } else {
                        dispatch(applyDiscount({ discount: 0 }));
                      }
                    }}
                    className="flex-1 px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm focus:ring-2 focus:ring-blue-500"
                    placeholder="0"
                  />
                </div>
              </div>
              
              {discount > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-400">Diskon</span>
                  <span className="text-red-400 font-medium">- Rp {discount.toLocaleString()}</span>
                </div>
              )}
              
              <div className="flex justify-between text-2xl font-bold pt-3 border-t border-gray-700">
                <span className="text-white">Total</span>
                <span className="text-green-400">Rp {total.toLocaleString()}</span>
              </div>
            </div>
          )}
          
          {/* Checkout Button */}
          <button
            onClick={handleCheckout}
            disabled={cartItems.length === 0 || loading}
            className="w-full py-4 bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white font-bold rounded-xl shadow-lg disabled:from-gray-600 disabled:to-gray-700 disabled:cursor-not-allowed transition-all transform hover:scale-105 disabled:scale-100 text-lg"
          >
            {loading ? '⏳ Processing...' : '💳 BAYAR SEKARANG'}
          </button>
        </div>
      </div>
      
      {/* Payment Modal */}
      {showPayment && (
        <PaymentModal
          total={finalTotal}
          onClose={() => setShowPayment(false)}
          onComplete={handlePaymentComplete}
        />
      )}
      
      {/* Receipt Modal */}
      {showReceipt && receiptData && (
        <ReceiptModal
          receipt={receiptData}
          isOpen={showReceipt}
          onClose={handleCloseReceipt}
        />
      )}
      
      {/* Barcode Scanner Modal */}
      <BarcodeScanner
        isOpen={showScanner}
        onScan={handleBarcodeScanned}
        onClose={() => setShowScanner(false)}
      />
      
      {/* Printer Settings Modal */}
      {showPrinterSettings && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 bg-black/50 z-50"
            onClick={() => setShowPrinterSettings(false)}
          />
          
          {/* Settings Panel */}
          <div className="fixed right-4 top-20 w-96 bg-gray-900 rounded-xl shadow-2xl z-50 border border-gray-700">
            <div className="p-6">
              {/* Header */}
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold text-white flex items-center gap-2">
                  🖨️ Printer Settings
                </h3>
                <button
                  onClick={() => setShowPrinterSettings(false)}
                  className="text-gray-400 hover:text-white"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              {/* Printer Status */}
              <div className={`p-4 rounded-lg mb-4 ${
                printerConnected 
                  ? 'bg-green-900/30 border border-green-700' 
                  : 'bg-red-900/30 border border-red-700'
              }`}>
                <div className="flex items-center gap-3">
                  <span className="text-2xl">{printerConnected ? '✅' : '❌'}</span>
                  <div className="flex-1">
                    <div className="font-medium text-white">
                      {printerConnected ? 'Printer Terhubung' : 'Printer Tidak Terhubung'}
                    </div>
                    {printerInfo && (
                      <div className="text-sm text-gray-400 mt-1">
                        {printerInfo}
                        {printerType && (
                          <span className="ml-2 px-2 py-0.5 bg-blue-600 text-white text-xs rounded">
                            {printerType === 'usb' ? 'USB' : 'Bluetooth'}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
              
              {/* Auto-Print Toggle */}
              <div className="mb-4 p-4 bg-gray-800 rounded-lg">
                <label className="flex items-center justify-between cursor-pointer">
                  <div className="flex items-center gap-3">
                    <span className="text-lg">⚡</span>
                    <div>
                      <div className="font-medium text-white">Auto-Print</div>
                      <div className="text-xs text-gray-400">
                        Print otomatis setelah transaksi
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => setAutoPrint(!autoPrint)}
                    className={`relative w-14 h-8 rounded-full transition-colors ${
                      autoPrint ? 'bg-green-600' : 'bg-gray-600'
                    }`}
                  >
                    <span
                      className={`absolute top-1 left-1 w-6 h-6 rounded-full bg-white transition-transform ${
                        autoPrint ? 'translate-x-6' : 'translate-x-0'
                      }`}
                    />
                  </button>
                </label>
              </div>
              
              {/* Action Buttons */}
              <div className="space-y-2">
                {!printerConnected ? (
                  <>
                    <div className="text-sm font-medium text-gray-300 mb-2">
                      Pilih Jenis Printer:
                    </div>
                    
                    <button
                      onClick={handleConnectUSBPrinter}
                      className="w-full py-3 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-medium rounded-lg transition-all flex items-center justify-center gap-2"
                    >
                      <span>🔌</span>
                      <span>USB Printer</span>
                    </button>
                    
                    <button
                      onClick={handleConnectBluetoothPrinter}
                      className="w-full py-3 bg-gradient-to-r from-indigo-600 to-purple-700 hover:from-indigo-700 hover:to-purple-800 text-white font-medium rounded-lg transition-all flex items-center justify-center gap-2"
                    >
                      <span>📱</span>
                      <span>Bluetooth Printer</span>
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      onClick={handleTestPrint}
                      className="w-full py-3 bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white font-medium rounded-lg transition-all flex items-center justify-center gap-2"
                    >
                      <span>🧪</span>
                      <span>Test Print</span>
                    </button>
                    
                    <button
                      onClick={handleDisconnectPrinter}
                      className="w-full py-3 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white font-medium rounded-lg transition-all flex items-center justify-center gap-2"
                    >
                      <span>🔌</span>
                      <span>Putuskan Koneksi</span>
                    </button>
                  </>
                )}
              </div>
              
              {/* WhatsApp Fallback Info */}
              <div className="mt-4 p-3 bg-blue-900/30 border border-blue-700 rounded-lg">
                <div className="flex items-start gap-2">
                  <span className="text-lg">💡</span>
                  <div className="text-xs text-blue-200">
                    <strong>Fallback WhatsApp:</strong> Jika print gagal dan customer punya nomor HP, sistem akan menawarkan kirim nota via WhatsApp.
                  </div>
                </div>
              </div>
              
              {/* Supported Printers Info */}
              <div className="mt-4 p-3 bg-gray-800 rounded-lg">
                <div className="text-xs text-gray-400">
                  <strong className="text-white">USB Printers:</strong>
                  <ul className="mt-1 ml-4 list-disc space-y-1">
                    <li>Bixolon (SPP-R200, SPP-R300)</li>
                    <li>Epson (TM-T82, TM-T88)</li>
                    <li>Rongta (RPP320, RP58)</li>
                    <li>Star Micronics (TSP100)</li>
                  </ul>
                  
                  <strong className="text-white mt-2 block">Bluetooth Printers:</strong>
                  <ul className="mt-1 ml-4 list-disc space-y-1">
                    <li>Rongta RPP02N ⭐</li>
                    <li>bp-td110bt ⭐</li>
                    <li>MTP-II, MTP-III</li>
                    <li>ESC/POS compatible</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ModernPOSScreen;
